import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loading from '../../../components/Loading';
import { useLayoutFooter, useStepperContext } from '../../../layouts/PatientCreationLayout';
import { useSubmissionForm } from '../../../providers/patient/PatientSubmissionContext';
import { useToast } from '../../../providers/ToastProvider';
import { reportCaughtError } from '../../../services/errorReporting';
import { updatePatientCreation } from '../../../services/patient/creation';

// Placeholders for icons and logos
import cephIcon from '../../../assets/icons/ceph.svg';
import xrayIcon from '../../../assets/icons/xray.svg';
import FileUploadCard from '../../../components/upload/FileUploadCard';
import MessageOdoFooterButton from '../../../components/patient/MessageOdoFooterButton';
import { MANUFACTURERS } from '../../../utils/constants';

// Pre-selected manufacturer when the patient has no saved choice yet.
const DEFAULT_MANUFACTURER = 'Spark Aligners';

const UploadPatientRadios: React.FC = () => {
    const { goNext, goToStep, isV2 } = useStepperContext();
    const { formData, updateSubmissionForm, isLoading: isFormLoading } = useSubmissionForm();
    const { setFooterContent } = useLayoutFooter();
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [isCbctUploading, setIsCbctUploading] = useState(false);
    const [isRadiographUploading, setIsRadiographUploading] = useState(false);
    const [cbctFileCount, setCbctFileCount] = useState(0);
    const [radiographFileCount, setRadiographFileCount] = useState(0);

    const handleCbctUploadStateChange = useMemo(
        () => (uploading: boolean) => setIsCbctUploading(uploading),
        []
    );
    const handleRadiographUploadStateChange = useMemo(
        () => (uploading: boolean) => setIsRadiographUploading(uploading),
        []
    );
    const handleCbctFilesChange = useCallback(
        (files: { id: string }[]) => setCbctFileCount(files.length),
        []
    );
    const handleRadiographFilesChange = useCallback(
        (files: { id: string }[]) => setRadiographFileCount(files.length),
        []
    );

    const caseTypeLabel = formData?.caseType?.label;
    const subCaseTitle = formData?.subCaseType?.title;

    const handleAdditionalInfoChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setIsModified(true);
            updateSubmissionForm({ additionalInfo: e.target.value });
        },
        [updateSubmissionForm]
    );

    const handleManufacturerChange = useCallback(
        (manufacturer: 'Invisalign Aligners' | 'Spark Aligners' | 'Select') => {
            setIsModified(true);
            updateSubmissionForm({ manufacturer });
        },
        [updateSubmissionForm]
    );

    // Preset Spark when the patient has no saved manufacturer ('Select' is the
    // legacy unset placeholder). A stored choice — either brand — always wins.
    // Routed through handleManufacturerChange so isModified is set and the
    // default persists on Next exactly like a manual selection.
    useEffect(() => {
        if (isFormLoading || !formData) return;
        if (formData.manufacturer && formData.manufacturer !== 'Select') return;
        handleManufacturerChange(DEFAULT_MANUFACTURER);
    }, [isFormLoading, formData, handleManufacturerChange]);

    const handleNextClick = useCallback(async () => {
        setIsLoading(true);
        try {
            if (isModified && formData?.id) {
                await updatePatientCreation(formData.id, {
                    manufacturer: formData.manufacturer,
                    additionalInfo: formData.additionalInfo ?? ''
                });
                setIsModified(false);
            }
            goNext();
        } catch (error) {
            showToast('Failed to save treatment plan. Please try again.', 'error');
            reportCaughtError(error);
        } finally {
            setIsLoading(false);
        }
    }, [goNext, formData, isModified, showToast]);

    useEffect(() => {
        const isAdditionalInfoEmpty =
            !formData?.additionalInfo || formData.additionalInfo.trim() === '';
        const isManufacturerSelected =
            !!formData?.manufacturer && formData.manufacturer !== 'Select';
        const hasUploadedFiles = cbctFileCount > 0 || radiographFileCount > 0;
        const isButtonDisabled =
            isLoading ||
            isAdditionalInfoEmpty ||
            !isManufacturerSelected ||
            !hasUploadedFiles ||
            isCbctUploading ||
            isRadiographUploading;

        setFooterContent(
            <div className="w-full flex justify-end">
                <div className="w-full flex justify-start items-center gap-2">
                    {!isV2 && caseTypeLabel && (
                        <>
                            <p className="text-m font-bold text-gray-500">Selected :</p>
                            <p className="text-m text-gray-500">{caseTypeLabel}</p>
                            {subCaseTitle && (
                                <p className="text-m text-gray-500"> - {subCaseTitle}</p>
                            )}
                        </>
                    )}
                </div>
                <div className="shrink-0 flex items-center gap-3">
                    <MessageOdoFooterButton />
                    <button
                        disabled={isButtonDisabled}
                        onClick={handleNextClick}
                        className={`flex flex-row justify-center items-center px-4 py-3.5 gap-2 min-w-32 h-10 rounded-lg font-bold text-sm leading-4 text-center tracking-wider transition-colors ${
                            isButtonDisabled
                                ? 'bg-gray-300 text-white cursor-not-allowed'
                                : 'bg-gray-800 text-white hover:bg-gray-700'
                        }`}
                    >
                        {isCbctUploading || isRadiographUploading ? (
                            <span className="flex items-center gap-1">
                                Uploading
                                <span className="flex gap-0.5">
                                    <span
                                        className="animate-bounce"
                                        style={{ animationDelay: '0ms' }}
                                    >
                                        .
                                    </span>
                                    <span
                                        className="animate-bounce"
                                        style={{ animationDelay: '150ms' }}
                                    >
                                        .
                                    </span>
                                    <span
                                        className="animate-bounce"
                                        style={{ animationDelay: '300ms' }}
                                    >
                                        .
                                    </span>
                                </span>
                            </span>
                        ) : (
                            'Continue'
                        )}
                    </button>
                </div>
            </div>
        );

        return () => {
            setFooterContent(null);
        };
    }, [
        setFooterContent,
        isLoading,
        handleNextClick,
        formData,
        isV2,
        caseTypeLabel,
        subCaseTitle,
        isCbctUploading,
        isRadiographUploading,
        cbctFileCount,
        radiographFileCount
    ]);

    if (!formData || (!isV2 && (!formData.caseType || !formData.subCaseType))) {
        return (
            <div className="text-center">
                <p>Missing patient data. Please start over.</p>
                <button
                    onClick={() => goToStep(0)}
                    className="mt-4 px-4 py-2 bg-gray-800 text-white rounded-lg"
                >
                    Start Over
                </button>
            </div>
        );
    }

    return (
        <>
            {isLoading && <Loading text="Saving..." />}

            <div className="flex flex-row justify-center items-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Review treatment plan</h1>
            </div>
            <div className="max-w-4xl mx-auto p-4">
                {/* Recap Box */}
                {!isV2 && (
                    <div className="box-border w-full max-w-[100%] flex flex-col border-2 border-yellow-400 bg-yellow-50 rounded-xl drop-shadow-sm overflow-hidden mb-8">
                        <div className="flex flex-row items-center p-3 w-full bg-yellow-400">
                            <h3 className="font-bold text-base leading-6 tracking-[0.005em] text-gray-900">
                                {caseTypeLabel}
                                {subCaseTitle ? ` - ${subCaseTitle}` : ''}
                            </h3>
                        </div>
                        <div className="flex flex-col justify-center items-start px-5 py-5 gap-3 w-full">
                            <h4 className="font-bold text-sm leading-4 tracking-[0.005em] text-gray-800">
                                Treatment Plan
                            </h4>
                            <ul className="list-disc list-inside px-2 space-y-3 text-sm text-[#6B7280]">
                                {formData.subCaseType &&
                                    Object.keys(formData.subCaseType.benefits).map(
                                        (benefit: string) => (
                                            <li key={`b-${benefit}`}>
                                                <span className="font-bold">{benefit} : </span>
                                                <span className="font-normal">
                                                    {formData.subCaseType?.benefits[benefit]}
                                                </span>
                                            </li>
                                        )
                                    )}
                            </ul>
                        </div>
                    </div>
                )}

                {/* File Upload */}
                <div className="mb-8 flex flex-col gap-2 max-w-full">
                    <h2 className="text-xl font-bold">Upload medical images *</h2>
                    <p className="text-gray-600">
                        Upload at least one Radiograph or CBCT for treatment planning
                    </p>
                    <p className="text-gray-600 mb-4">
                        <span className="text-gray-900 underline italic font-bold">
                            {' '}
                            Important :
                        </span>{' '}
                        If the patient has been previously treated, then only submit records that
                        have been taken after their most recent treatment.
                        {formData.caseType?.monday_key === 'Pediatrics' && (
                            <span>
                                {' '}
                                If your patient has teeth erupting or you are aware of potential
                                shifting, please upload records no older than 4 weeks for an
                                accurate diagnosis and treatment plan.
                            </span>
                        )}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
                        <FileUploadCard
                            label="Radiographs"
                            icon={xrayIcon}
                            patientId={formData?.id}
                            isOptional={true}
                            hideIndicator={true}
                            fileType="RADIOGRAPH"
                            accept="image/*,.pdf"
                            onUploadStateChange={handleRadiographUploadStateChange}
                            onUploadedFilesChange={handleRadiographFilesChange}
                        />
                        <FileUploadCard
                            label="CBCT"
                            isOptional={true}
                            patientId={formData?.id}
                            fileType="CBCT"
                            accept=".dcm,.dicom,image/*,.zip,.rar"
                            icon={cephIcon}
                            onUploadStateChange={handleCbctUploadStateChange}
                            onUploadedFilesChange={handleCbctFilesChange}
                        />
                    </div>
                </div>

                {/* Additional Information */}
                <div className="mb-8">
                    <h2 className="text-xl font-bold mb-2">Additional information * </h2>
                    <textarea
                        value={formData.additionalInfo || ''}
                        onChange={handleAdditionalInfoChange}
                        placeholder="Add any information like treatment goals..."
                        className={`w-full h-32 p-4 border rounded-lg ${
                            !formData?.additionalInfo || formData.additionalInfo.trim() === ''
                                ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                                : 'border-gray-300 focus:border-gray-500 focus:ring-gray-200'
                        }`}
                    ></textarea>
                </div>

                {/* Manufacturer Selection */}
                <div className="mb-8">
                    <h2 className="text-xl font-bold mb-4">Select your aligner manufacturer *</h2>
                    <div className="flex gap-24 justify-center">
                        {MANUFACTURERS.map((manu) => (
                            <button
                                key={manu.id}
                                onClick={() => handleManufacturerChange(manu.id)}
                                className={`border-2 p-6 rounded-lg flex items-center gap-4 w-64 h-24 ${formData.manufacturer === manu.id ? 'border-yellow-400' : 'border-gray-300'}`}
                            >
                                <div
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${formData.manufacturer === manu.id ? 'border-gray-900' : 'border-gray-300'}`}
                                >
                                    {formData.manufacturer === manu.id && (
                                        <div className="w-2.5 h-2.5 rounded-full bg-gray-900" />
                                    )}
                                </div>
                                <img src={manu.logo} alt={manu.name} className={manu.className} />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default UploadPatientRadios;
